import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Navigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { useToast } from '../hooks/use-toast';
import { Loader2, Plus, Trash2, AlertTriangle, RefreshCw, CalendarDays, X, Download } from 'lucide-react';
import { EditableHeader, InvoiceRow, DeleteInvoiceDialog } from '../components/DailyInvoiceBits';
import { RepeatInvoiceDialog, RecurringSchedulesCard, DraftsQueueCard } from '../components/RecurringInvoices';
import { DailyInvoiceTour } from '../components/DailyInvoiceTour';
import { useNavigate } from 'react-router-dom';
import { readCompanyDefaults, fetchCompanyDefaults } from '../hooks/useCompanyDefaults';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const hhmm = (h) => { const n = Number(h || 0); const w = Math.floor(n); const m = Math.round((n - w) * 60); return `${String(w).padStart(2, '0')}:${String(m).padStart(2, '0')}`; };
const parseHours = (s) => { if (s == null) return 0; const t = String(s).trim(); if (/^\d{1,2}:\d{2}$/.test(t)) { const [h, m] = t.split(':').map(Number); return h + m / 60; } return Number(t) || 0; };
const money = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const inputStyle = { background: '#1A2433', color: '#fff', border: '1px solid #2D3748' };

export default function DailyInvoices() {
  const { user, token } = useAuth();
  const { toast } = useToast();
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [jobs, setJobs] = useState([]);
  const [jobId, setJobId] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [dateOpen, setDateOpen] = useState(false);
  const [shift, setShift] = useState('12');
  const [preview, setPreview] = useState(null);
  const [lines, setLines] = useState([]);
  const [meta, setMeta] = useState({ invoice_number: '', terms_days: 30, po_number: '', site_name: '', work_order: '', tax_rate_pct: 0, tax_label: 'GST', notes: '' });
  const [busy, setBusy] = useState(false);
  const [list, setList] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [colLabels, setColLabels] = useState({});
  const [showArchived, setShowArchived] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [toRepeat, setToRepeat] = useState(null);
  const [recurringKey, setRecurringKey] = useState(0);
  const [selected, setSelected] = useState({});
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState('');
  const [nextNumber, setNextNumber] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/jobs`, auth).then((r) => setJobs((r.data || []).filter((j) => !j.deleted_at))).catch(() => {});
  }, [token]);

  const loadList = useCallback(() => axios.get(`${API}/daily-invoices`, { ...auth, params: { include_archived: showArchived } }).then((r) => setList(r.data.invoices || [])).catch(() => {}),
    [token, showArchived]);
  useEffect(() => { if (token) loadList(); }, [token, loadList]);
  useEffect(() => {
    if (!token) return;
    Promise.all([
      axios.get(`${API}/company/document-presets`, auth),
      fetchCompanyDefaults(token).catch(() => readCompanyDefaults()),
    ]).then(([r, cd]) => {
      const presets = r.data.presets || [];
      setTemplates(presets);
      // Company default template (Settings → Company Defaults → Invoices) pre-selected for new invoices.
      const def = presets.find((x) => x.id === cd.invoices_default_template_id);
      if (def) applyTemplateObj(def, { silent: true });
    }).catch(() => {});
  }, [token]);
  useEffect(() => {
    if (!token || !preview) return;
    axios.get(`${API}/daily-invoices/next-number`, { ...auth, params: templateId ? { template_id: templateId } : {} }).then((r) => setNextNumber(r.data.next_number || '')).catch(() => setNextNumber(''));
  }, [token, preview, templateId, list]);

  const applyTemplate = (id) => applyTemplateObj(templates.find((x) => x.id === id), { id });
  const applyTemplateObj = (t, { id = t?.id || '', silent = false } = {}) => {
    setTemplateId(id);
    if (!t) { setColLabels({}); return; }
    const d = t.defaults || {};
    setMeta((m) => ({ ...m, ...(d.terms_days != null ? { terms_days: d.terms_days } : {}), ...(d.tax_label ? { tax_label: d.tax_label } : {}),
      ...(d.tax_rate_pct != null ? { tax_rate_pct: d.tax_rate_pct } : {}), ...(d.notes ? { notes: d.notes } : {}), ...(d.site_name ? { site_name: d.site_name } : {}), invoice_number: '' }));
    const cols = (t.columns || {}).daily;
    setColLabels(cols?.length ? Object.fromEntries(cols.map((c) => [c.key, c.label])) : {});
    if (!silent) toast({ title: `Template “${t.name}” applied`, description: t.numbering ? 'Uses the template’s own invoice number sequence.' : 'Defaults and headers filled in — adjust anything before creating.' });
  };

  const runPreview = async () => {
    if (!jobId) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/daily-invoices/preview`, { job_id: jobId, date, shift_hours: Number(shift) || undefined }, auth);
      setPreview(data); setEditingId(null); setColLabels({});
      setLines(data.lines.map((l) => ({ ...l, hoursText: hhmm(l.hours) })));
      setMeta((m) => ({ ...m, terms_days: data.customer.terms_days || 30, po_number: data.customer.po_number || '', site_name: data.customer.site_name || '' }));
      // Keep the chosen (or company-default) template's defaults/headers on top of the customer values.
      const tpl = templates.find((x) => x.id === templateId);
      if (tpl) applyTemplateObj(tpl, { silent: true });
      if (data.missing_rate_card?.length) toast({ title: 'Rate card missing codes', description: data.missing_rate_card.map((x) => `${x.worker}: ${x.bill_class}`).join(', '), variant: 'destructive' });
    } catch (e) { toast({ title: 'Preview failed', description: e.response?.data?.detail || e.message, variant: 'destructive' }); }
    finally { setBusy(false); }
  };

  const setLine = (i, patch) => setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const applyClass = (i, code) => {
    const rc = (preview?.customer?.rate_card || []).find((r) => r.code === code);
    setLine(i, { bill_class: code, bill_name: rc?.name || (code ? `Standard Pay ${code}` : ''), rate: rc?.rate ?? lines[i].rate, badge_code: rc?.badge_code || lines[i].badge_code });
  };
  const subtotal = lines.reduce((s, l) => s + parseHours(l.hoursText) * Number(l.rate || 0), 0);
  const tax = Math.round(subtotal * (Number(meta.tax_rate_pct) || 0)) / 100;

  const resetBuilder = () => { setPreview(null); setLines([]); setEditingId(null); setColLabels({}); setMeta((m) => ({ ...m, invoice_number: '', work_order: '', notes: '' })); };
  const create = async () => {
    setBusy(true);
    try {
      const cols = Object.keys(colLabels).length ? columns : null;
      const payload = { job_id: jobId, date, lines: lines.map((l) => ({ ...l, hours: parseHours(l.hoursText), rate: Number(l.rate || 0) })),
        invoice_number: meta.invoice_number || null, terms_days: Number(meta.terms_days) || 0, po_number: meta.po_number || null, site_name: meta.site_name || null,
        work_order: meta.work_order || null, notes: meta.notes || null, tax_rate_pct: Number(meta.tax_rate_pct) || 0, tax_label: meta.tax_label || null, currency: preview?.currency,
        columns: cols || (editingId ? [] : null), template_id: templateId || null };
      if (editingId) {
        const { data } = await axios.patch(`${API}/daily-invoices/${editingId}`, payload, auth);
        toast({ title: `Invoice ${data.invoice_number} updated`, description: `${data.lines.length} lines · ${data.currency} ${money(data.total)} — PDF/CSV/XLSX regenerate on download` });
      } else {
        const { data } = await axios.post(`${API}/daily-invoices`, payload, auth);
        toast({ title: `Invoice ${data.invoice_number} created`, description: `${data.lines.length} lines · ${data.currency} ${money(data.total)}` });
      }
      resetBuilder();
      loadList();
    } catch (e) { toast({ title: editingId ? 'Could not save changes' : 'Could not create invoice', description: e.response?.data?.detail || e.message, variant: 'destructive' }); }
    finally { setBusy(false); }
  };

  const startEdit = async (inv) => {
    setBusy(true);
    try {
      const [{ data: full }, { data: pv }] = await Promise.all([
        axios.get(`${API}/daily-invoices/${inv.id}`, auth),
        axios.post(`${API}/daily-invoices/preview`, { job_id: inv.job_id, date: inv.service_date }, auth),
      ]);
      setJobId(inv.job_id); setDate(inv.service_date);
      setPreview({ ...pv, currency: full.currency || pv.currency, columns: full.columns?.length ? full.columns : pv.columns });
      setColLabels(full.columns?.length ? Object.fromEntries(full.columns.map((c) => [c.key, c.label])) : {});
      setLines((full.lines || []).map((l) => ({ ...l, hoursText: hhmm(l.hours), manual: !l.worker_id })));
      const termsDays = /^Net (\d+)$/.exec(full.terms || '')?.[1];
      setMeta({ invoice_number: full.invoice_number || '', terms_days: termsDays ? Number(termsDays) : 0, po_number: full.po_number || '', site_name: full.site_name || '',
        work_order: full.work_order || '', tax_rate_pct: full.tax_rate_pct || 0, tax_label: full.tax_label || '', notes: full.notes || '' });
      setEditingId(inv.id);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) { toast({ title: 'Could not open invoice', description: e.response?.data?.detail || e.message, variant: 'destructive' }); }
    finally { setBusy(false); }
  };

  const archive = async (inv, on) => {
    try {
      await axios.patch(`${API}/daily-invoices/${inv.id}`, { status: on ? 'archived' : 'issued' }, auth);
      toast({ title: on ? `Invoice ${inv.invoice_number} archived` : `Invoice ${inv.invoice_number} restored`, description: on ? 'Hidden from the main list — turn on "Show archived" to see it.' : undefined });
      if (editingId === inv.id) resetBuilder();
      loadList();
    } catch (e) { toast({ title: 'Could not update invoice', description: e.response?.data?.detail || e.message, variant: 'destructive' }); }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/daily-invoices/${toDelete.id}`, auth);
      toast({ title: `Invoice ${toDelete.invoice_number} deleted`, description: 'A deletion record was kept in the audit log.' });
      if (editingId === toDelete.id) resetBuilder();
      setToDelete(null); loadList();
    } catch (e) { toast({ title: 'Could not delete invoice', description: e.response?.data?.detail || e.message, variant: 'destructive' }); }
    finally { setBusy(false); }
  };

  const duplicate = async (inv) => {
    const today = new Date().toISOString().slice(0, 10);
    const d = window.prompt(`Copy ${inv.invoice_number} (same crew, rates and headers) to which service date? (YYYY-MM-DD)`, today);
    if (!d) return;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(d.trim())) { toast({ title: 'Date must be YYYY-MM-DD', variant: 'destructive' }); return; }
    try {
      const { data } = await axios.post(`${API}/daily-invoices/${inv.id}/duplicate`, { date: d.trim() }, auth);
      toast({ title: `Invoice ${data.invoice_number} created`, description: `Copy of ${inv.invoice_number} for ${data.service_date} — opened in the builder so you can adjust hours.` });
      await loadList();
      startEdit(data);
    } catch (e) { toast({ title: 'Could not duplicate invoice', description: e.response?.data?.detail || e.message, variant: 'destructive' }); }
  };

  const selectedIds = Object.keys(selected).filter((k) => selected[k]);
  const exportSelected = async () => {
    if (!selectedIds.length) return;
    try {
      const r = await axios.post(`${API}/daily-invoices/export.xlsx`, { ids: selectedIds }, { ...auth, responseType: 'blob' });
      const name = /filename="([^"]+)"/.exec(r.headers['content-disposition'] || '')?.[1] || `Invoices-${selectedIds.length}.xlsx`;
      const url = URL.createObjectURL(r.data); const a = document.createElement('a'); a.href = url; a.download = name; a.click(); URL.revokeObjectURL(url);
      toast({ title: `Exported ${selectedIds.length} invoice${selectedIds.length === 1 ? '' : 's'}`, description: 'One workbook: a Lines sheet ready for upload plus a Summary sheet.' });
    } catch (e) { toast({ title: 'Export failed', description: e.response?.data?.detail || e.message, variant: 'destructive' }); }
  };

  const renameColumn = (key, label) => setColLabels((m) => ({ ...m, [key]: label }));
  const saveHeadersAsDefault = async () => {
    const cid = preview?.customer?.id;
    if (!cid) { toast({ title: 'No customer on this job', description: 'Attach a customer to the job to save default headers.', variant: 'destructive' }); return; }
    try {
      const items = (preview.customer.rate_card || []).map(({ code, name, rate, badge_code }) => ({ code, name, rate, badge_code }));
      await axios.put(`${API}/customers/${cid}/rate-card`, { items, column_layouts: { daily: columns }, change_note: 'Headers set from the Daily Invoices builder' }, auth);
      toast({ title: 'Headers saved for this customer', description: `${preview.customer.name}'s next daily invoices start with these column names.` });
    } catch (e) { toast({ title: 'Could not save headers', description: e.response?.data?.detail || e.message, variant: 'destructive' }); }
  };

  const download = async (inv, kind) => {
    try {
      const r = await axios.get(`${API}/daily-invoices/${inv.id}/${kind}`, { ...auth, responseType: 'blob' });
      const url = URL.createObjectURL(r.data); const a = document.createElement('a'); a.href = url; a.download = `Invoice-${inv.invoice_number}.${kind}`; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast({ title: 'Download failed', variant: 'destructive' }); }
  };
  const renumber = async (inv) => {
    const n = window.prompt('Invoice number', inv.invoice_number);
    if (!n || n === inv.invoice_number) return;
    const reason = window.prompt('Reason for the change (optional — recorded in Admin → Audit Log):', '') || '';
    try { await axios.patch(`${API}/daily-invoices/${inv.id}`, { invoice_number: n, renumber_reason: reason }, auth); loadList(); }
    catch (e) { toast({ title: 'Could not renumber', description: e.response?.data?.detail || e.message, variant: 'destructive' }); }
  };

  if (user && user.role !== 'admin' && !user.is_super_user) return <Navigate to="/dashboard" replace />;

  const DEFAULT_COLS = [
    { key: 'bill_name', label: 'NAME', source: 'bill_name', align: 'left' }, { key: 'description', label: 'DESCRIPTION', source: 'description', align: 'left' },
    { key: 'hours_hhmm', label: 'QTY', source: 'hours_hhmm', align: 'right' }, { key: 'rate', label: 'RATE', source: 'rate', align: 'right' },
    { key: 'amount', label: 'AMOUNT', source: 'amount', align: 'right' }, { key: 'badge', label: 'BADGE #', source: 'badge', align: 'left' },
  ];
  const columns = (preview?.columns?.length ? preview.columns : DEFAULT_COLS).filter((c) => c.source !== 'date').map((c) => (colLabels[c.key] ? { ...c, label: colLabels[c.key] } : c));
  const workerCell = (l, i) => (l.manual ? (
    <div className="flex gap-1">
      <Input placeholder="Employee ID" value={l.employee_number} onChange={(e) => setLine(i, { employee_number: e.target.value })} className="h-9 w-24" style={inputStyle} data-testid={`di-line-empno-${i}`} />
      <Input placeholder="Worker name" value={l.worker_name} onChange={(e) => setLine(i, { worker_name: e.target.value })} className="h-9 w-36" style={inputStyle} data-testid={`di-line-name-${i}`} />
    </div>
  ) : (<span className="text-white whitespace-nowrap">{l.employee_number ? `${l.employee_number}-` : <span className="text-[10px] px-1.5 py-0.5 rounded mr-1" style={{ color: '#FBBF24', border: '1px solid rgba(251,191,36,0.5)' }} title="No employee number on this worker (Manage Users → edit)" data-testid={`di-line-noid-${i}`}>No ID</span>}{l.worker_name}</span>));
  const classCell = (l, i) => (
    <Select value={l.bill_class || '__none__'} onValueChange={(v) => applyClass(i, v === '__none__' ? '' : v)}>
      <SelectTrigger className="h-9 text-xs w-44" style={inputStyle} data-testid={`di-line-class-${i}`}><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="__none__" data-testid={`di-line-class-${i}-opt-none`}>— no code —</SelectItem>
        {(preview?.customer?.rate_card || []).map((r) => <SelectItem key={r.code} value={r.code} data-testid={`di-line-class-${i}-opt-${r.code}`}>{r.name} ({r.code})</SelectItem>)}
        {l.bill_class && !(preview?.customer?.rate_card || []).some((r) => r.code === l.bill_class) && <SelectItem value={l.bill_class}>{l.bill_class} (not on rate card)</SelectItem>}
      </SelectContent>
    </Select>
  );
  const renderCell = (c, l, i) => {
    switch (c.source) {
      case 'bill_name': case 'bill_code': return classCell(l, i);
      case 'description': return workerCell(l, i);
      case 'worker_name': return l.manual ? <Input placeholder="Worker name" value={l.worker_name} onChange={(e) => setLine(i, { worker_name: e.target.value })} className="h-9 w-36" style={inputStyle} data-testid={`di-line-name-${i}`} /> : <span className="text-white">{l.worker_name}</span>;
      case 'employee_number': return l.manual ? <Input placeholder="Employee ID" value={l.employee_number} onChange={(e) => setLine(i, { employee_number: e.target.value })} className="h-9 w-24" style={inputStyle} data-testid={`di-line-empno-${i}`} /> : <span className="text-white">{l.employee_number || '—'}</span>;
      case 'hours_hhmm': return <Input value={l.hoursText} onChange={(e) => setLine(i, { hoursText: e.target.value })} className="h-9 w-20" style={inputStyle} data-testid={`di-line-hours-${i}`} />;
      case 'hours_decimal': return <span className="text-white" data-testid={`di-line-hoursdec-${i}`}>{parseHours(l.hoursText).toFixed(2)}</span>;
      case 'rate': return <Input type="number" step="0.01" value={l.rate} onChange={(e) => setLine(i, { rate: e.target.value })} className="h-9 w-24" style={inputStyle} data-testid={`di-line-rate-${i}`} />;
      case 'amount': return <span className="text-white" data-testid={`di-line-amount-${i}`}>{money(parseHours(l.hoursText) * Number(l.rate || 0))}</span>;
      case 'badge': return <Input value={l.badge_code || ''} onChange={(e) => setLine(i, { badge_code: e.target.value })} className="h-9 w-36" style={inputStyle} data-testid={`di-line-badge-${i}`} />;
      case 'custom': return <Input value={(l.custom_fields || {})[c.key] || ''} placeholder={c.label} onChange={(e) => setLine(i, { custom_fields: { ...(l.custom_fields || {}), [c.key]: e.target.value } })} className="h-9 w-36" style={inputStyle} data-testid={`di-line-custom-${c.key}-${i}`} />;
      default: return null;
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-5 max-w-6xl mx-auto" data-testid="daily-invoices-page">
      <div>
        <h1 className="text-2xl font-semibold text-white">Daily Site Invoices</h1>
        <p className="text-sm mt-0.5" style={{ color: '#A0AEC0' }}>One invoice per job site per day — a line for every worker on shift, with bill code, rate and badge # — ready for PDF or CSV upload to your customer's billing platform.</p>
        <button onClick={() => navigate('/daily-invoices?tour=1')} className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold hover:underline" style={{ color: '#8BC34A' }} data-testid="di-start-tour">Guided walkthrough: build your first invoice →</button>
        <DailyInvoiceTour />
      </div>

      <div className="ht-card p-4 grid grid-cols-1 md:grid-cols-[1fr_160px_120px_auto] gap-3 items-end">
        <div>
          <label className="text-xs" style={{ color: '#A0AEC0' }}>Job site</label>
          <Select value={jobId} onValueChange={setJobId}>
            <SelectTrigger className="w-full h-10" style={inputStyle} data-testid="di-job-select">
              <SelectValue placeholder="Select a job…" />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              {jobs.map((j) => (
                <SelectItem key={j.id} value={j.id} data-testid={`di-job-option-${j.id}`}>
                  {j.job_number ? `${j.job_number} · ` : ''}{j.title}{j.customer_name ? ` — ${j.customer_name}` : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs" style={{ color: '#A0AEC0' }}>Service date</label>
          <Popover open={dateOpen} onOpenChange={setDateOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-full h-10 justify-start font-normal" style={inputStyle} data-testid="di-date">
                <CalendarDays className="w-4 h-4 mr-2 opacity-70" />{date || 'Pick a date'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={date ? new Date(`${date}T12:00:00`) : undefined}
                onSelect={(d) => { if (d) { setDate(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`); setDateOpen(false); } }}
                data-testid="di-date-calendar"
              />
            </PopoverContent>
          </Popover>
        </div>
        <div><label className="text-xs" style={{ color: '#A0AEC0' }}>Shift hours</label><Input type="number" step="0.25" min="0.25" max="24" value={shift} onChange={(e) => setShift(e.target.value)} style={inputStyle} data-testid="di-shift-hours" /></div>
        <Button onClick={runPreview} disabled={!jobId || busy} data-testid="di-preview-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1.5" />} Build lines</Button>
      </div>

      {preview && (
        <div className="ht-card p-4 space-y-4" data-testid="di-preview">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            {editingId && <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold" style={{ background: 'rgba(251,191,36,0.15)', color: '#FBBF24' }} data-testid="di-editing-badge">EDITING {meta.invoice_number}</span>}
            <span className="text-white font-semibold">{preview.customer.name || 'No customer'}</span>
            <span style={{ color: '#A0AEC0' }}>· {preview.job.title} · {preview.date} · {preview.currency}</span>
            <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: 'rgba(30,136,229,0.15)', color: '#93C5FD' }} data-testid="di-source">{preview.source === 'roster' ? 'From crew roster (no punches yet)' : 'From clock-ins on this job'}</span>
            {!preview.customer.rate_card?.length && <span className="inline-flex items-center gap-1 text-xs" style={{ color: '#FBBF24' }} data-testid="di-no-rate-card"><AlertTriangle className="w-3.5 h-3.5" /> No rate card on this customer — set it in Customers → edit</span>}
          </div>
          {lines.length === 0 && <p className="text-sm" style={{ color: '#FBBF24' }} data-testid="di-empty">No workers found for this job on {preview.date} — nobody clocked in and the job has no crew assigned. Add lines manually below.</p>}
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="di-lines-table">
              <thead><tr className="text-left text-[11px] uppercase tracking-wide" style={{ color: '#8BC34A' }}>
                {columns.map((c) => <th key={c.key} className={`py-2 pr-2 ${c.align === 'right' ? 'text-right' : ''}`} data-testid={`di-colcell-${c.key}`}><EditableHeader col={c} onRename={renameColumn} /></th>)}<th /></tr></thead>
              <tbody>
                {lines.map((l, i) => (
                  <tr key={i} className="border-t" style={{ borderColor: '#2D3748' }} data-testid={`di-line-${i}`}>
                    {columns.map((c) => <td key={c.key} className={`py-1.5 pr-2 ${c.align === 'right' ? 'text-right' : ''}`}>{renderCell(c, l, i)}</td>)}
                    <td className="py-1.5"><button onClick={() => setLines((ls) => ls.filter((_, idx) => idx !== i))} className="p-1.5" style={{ color: '#F87171' }} data-testid={`di-line-remove-${i}`}><Trash2 className="w-4 h-4" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button size="sm" variant="outline" onClick={() => setLines((ls) => [...ls, { worker_name: '', employee_number: '', bill_class: '', bill_name: '', rate: 0, hoursText: hhmm(preview.shift_hours || 12), badge_code: '', date: preview.date, manual: true }])} data-testid="di-add-line">
              <Plus className="w-4 h-4 mr-1" /> Add line
            </Button>
            <span className="text-xs" style={{ color: '#64748B' }} data-testid="di-headers-hint">Click a column header to rename it for this invoice.</span>
            {Object.keys(colLabels).length > 0 && (
              <>
                <button onClick={() => setColLabels({})} className="text-xs hover:underline" style={{ color: '#93C5FD' }} data-testid="di-headers-reset">Reset headers</button>
                <button onClick={saveHeadersAsDefault} className="text-xs hover:underline" style={{ color: '#8BC34A' }} data-testid="di-headers-save-default">Make default for this customer</button>
              </>
            )}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 border-t pt-4" style={{ borderColor: '#2D3748' }}>
            {templates.length > 0 && !editingId && (
              <div className="col-span-2 md:col-span-4"><label className="text-xs" style={{ color: '#A0AEC0' }}>Invoice template</label>
                <select value={templateId} onChange={(e) => applyTemplate(e.target.value)} className="w-full h-9 rounded-md px-2 text-sm" style={inputStyle} data-testid="di-template-select">
                  <option value="">— Company defaults —</option>
                  {templates.map((t) => <option key={t.id} value={t.id}>{t.name}{t.numbering ? ' · own numbering' : ''}</option>)}
                </select>
              </div>
            )}
            {[['invoice_number', `Invoice # (blank = ${nextNumber || 'next'})`, 'text'], ['terms_days', 'Terms (days)', 'number'], ['po_number', 'P.O. number', 'text'], ['site_name', 'Site', 'text'], ['work_order', 'Work order / SOW', 'text'], ['tax_rate_pct', 'Tax %', 'number'], ['tax_label', 'Tax label', 'text']].map(([k, lbl, type]) => (
              <div key={k}><label className="text-xs" style={{ color: '#A0AEC0' }}>{lbl}</label><Input type={type} value={meta[k]} placeholder={k === 'invoice_number' ? nextNumber : ''} onChange={(e) => setMeta({ ...meta, [k]: e.target.value })} style={inputStyle} data-testid={`di-meta-${k}`} /></div>
            ))}
            <div className="col-span-2 md:col-span-4"><label className="text-xs" style={{ color: '#A0AEC0' }}>Notes (printed under totals)</label><Input value={meta.notes} onChange={(e) => setMeta({ ...meta, notes: e.target.value })} style={inputStyle} data-testid="di-meta-notes" /></div>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm" style={{ color: '#A0AEC0' }}>Subtotal <span className="text-white font-semibold" data-testid="di-subtotal">{money(subtotal)}</span>{tax > 0 && <> · {meta.tax_label || 'Tax'} {money(tax)}</>} · <span className="text-white font-bold" data-testid="di-total">Total {preview.currency} {money(subtotal + tax)}</span></p>
            <div className="flex gap-2">
              {editingId && <Button variant="outline" onClick={resetBuilder} disabled={busy} data-testid="di-cancel-edit-btn"><X className="w-4 h-4 mr-1" /> Cancel</Button>}
              <Button onClick={create} disabled={busy || !lines.length} className="bg-[#8BC34A] hover:bg-[#7CB342] text-[#0B1321] font-semibold" data-testid="di-create-btn">{busy ? 'Saving…' : editingId ? 'Save changes' : 'Create invoice'}</Button>
            </div>
          </div>
        </div>
      )}

      <DraftsQueueCard token={token} refreshKey={recurringKey} onIssued={loadList} />
      <RecurringSchedulesCard token={token} refreshKey={recurringKey} />

      <div className="ht-card p-4" data-testid="di-list">
        <div className="flex items-center justify-between gap-3 mb-2">
          <p className="text-sm text-white font-semibold">Generated invoices</p>
          <div className="flex flex-wrap items-center gap-3">
            {list.length > 0 && (
              <button onClick={() => setSelected(selectedIds.length === list.length ? {} : Object.fromEntries(list.map((i) => [i.id, true])))} className="text-xs hover:underline" style={{ color: '#93C5FD' }} data-testid="di-select-all">
                {selectedIds.length === list.length ? 'Clear selection' : 'Select all'}
              </button>
            )}
            <Button size="sm" onClick={exportSelected} disabled={!selectedIds.length} className="bg-[#1E88E5] hover:bg-[#1976D2] text-white" data-testid="di-export-selected">
              <Download className="w-3.5 h-3.5 mr-1" /> Export selected{selectedIds.length ? ` (${selectedIds.length})` : ''} XLSX
            </Button>
            <label className="flex items-center gap-2 text-xs cursor-pointer" style={{ color: '#A0AEC0' }}>
              <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} className="accent-[#8BC34A]" data-testid="di-show-archived" /> Show archived
            </label>
          </div>
        </div>
        {list.length === 0 ? <p className="text-sm" style={{ color: '#A0AEC0' }}>None yet.</p> : (
          <div className="space-y-1.5">
            {list.map((inv) => <InvoiceRow key={inv.id} inv={inv} onRenumber={renumber} onDownload={download} onEdit={startEdit} onArchive={archive} onDelete={setToDelete} onDuplicate={duplicate} onRepeat={setToRepeat} selected={selected[inv.id]} onSelect={(id, on) => setSelected((m) => ({ ...m, [id]: on }))} />)}
          </div>
        )}
      </div>
      <DeleteInvoiceDialog inv={toDelete} onCancel={() => setToDelete(null)} onConfirm={confirmDelete} busy={busy} />
      {toRepeat && (
        <RepeatInvoiceDialog inv={toRepeat} token={token} onClose={() => setToRepeat(null)}
          onCreated={(s) => { setToRepeat(null); setRecurringKey((k) => k + 1); toast({ title: `Repeating ${s.source_invoice_number}`, description: `First run ${s.next_run} · ${s.frequency} · created as ${s.create_as}. Admins are emailed on each run.` }); }} />
      )}
    </div>
  );
}
